import type { JourneyPageSectionRow } from "@/lib/api";

export type VerdictId = "ok" | "uwaga" | "do_poprawy" | "brak";

export type SectionAudit = {
  section: JourneyPageSectionRow;
  label: string;
  dropPp: number | null;
  verdict: VerdictId;
  reason: string;
};

const OFFER_SECTIONS = new Set([
  "cennik",
  "kontakt",
  "oferta",
  "kup",
  "final",
  "pakiet",
]);

export function sectionLabel(
  sectionId: string,
  labels: Record<string, string> | undefined,
): string {
  return labels?.[sectionId] || sectionId;
}

/** Heurystyki OK / uwaga / do poprawy dla kolumny Landingi. */
export function buildSectionAudits(
  sections: JourneyPageSectionRow[],
  labels: Record<string, string> | undefined,
): SectionAudit[] {
  const hasAnyCta = sections.some((s) => (s.cta_clicks ?? 0) > 0);
  const hero = sections.find((s) => s.section_id === "hero");
  const heroOk = (hero?.view_rate ?? 0) >= 0.7;

  return sections.map((section, index) => {
    const prev = index > 0 ? sections[index - 1] : null;
    const dropPp =
      prev != null && prev.view_rate != null && section.view_rate != null
        ? (prev.view_rate - section.view_rate) * 100
        : null;

    let verdict: VerdictId = "ok";
    let reason = "Zasięg i CTA w normie względem poprzedniej sekcji.";

    if (section.sessions_reached <= 0 && (section.view_rate ?? 0) <= 0) {
      verdict = "brak";
      reason = "Brak sygnału w tym okresie.";
    } else if (dropPp != null && dropPp >= 25) {
      verdict = "do_poprawy";
      reason = `Duży spadek dojścia: −${Math.round(dropPp)} pp względem poprzedniej sekcji.`;
    } else if (
      hasAnyCta &&
      (section.view_rate ?? 0) >= 0.3 &&
      (section.cta_rate_of_viewers ?? 0) < 0.005 &&
      (section.cta_clicks ?? 0) === 0 &&
      OFFER_SECTIONS.has(section.section_id)
    ) {
      verdict = "do_poprawy";
      reason = "Sekcja ofertowa z sensownym reach, bez kliknięć CTA.";
    } else if (dropPp != null && dropPp >= 12) {
      verdict = "uwaga";
      reason = `Spadek dojścia: −${Math.round(dropPp)} pp względem poprzedniej sekcji.`;
    } else if (
      heroOk &&
      section.section_id !== "hero" &&
      (section.view_rate ?? 0) > 0 &&
      (section.view_rate ?? 0) < 0.35 &&
      index >= 2 &&
      index < sections.length - 2
    ) {
      verdict = "uwaga";
      reason = "Słaby reach w środku lejka przy zdrowym hero.";
    } else if ((section.cta_clicks ?? 0) === 0 && !hasAnyCta) {
      reason = "Brak CTA na całej stronie w tym okresie; oceniamy tylko zasięg.";
    }

    return {
      section,
      label: sectionLabel(section.section_id, labels),
      dropPp,
      verdict,
      reason,
    };
  });
}

export function iframeUrl(liveUrl: string, sectionId: string | null): string {
  const base = liveUrl.replace(/#.*$/, "");
  if (!sectionId || sectionId === "(poza sekcją)") return base;
  return `${base}#${encodeURIComponent(sectionId)}`;
}
